import React, { useState, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Col, Form, Button, Divider } from "antd";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import moment from "moment";

import {
  put,
  post,
  checkResponse,
  checkHttpStatus,
} from "../../modules/httpFunctions";
import InfoLabel from "../app/generic/infoLabel";
import InputItem from "../app/generic/inputItem";
import TextEditor from "../app/generic/textEditor";
import TableHeader from "../app/generic/tableHeader";
import CheckboxItem from "../app/generic/checkboxItem";
import { handleError } from "../../modules/errorLogger";
import { error, success, warn } from "../../modules/messages";
import DateItem, { getDateFromDatePicker } from "../app/generic/dateItem";

const Notification = ({ push, form, notification }) => {
  const intl = useIntl();
  const { getFieldDecorator } = form;
  const formItemLayout = { colon: false };

  const [loading, setLoading] = useState();
  const [message, setMessage] = useState(notification && notification.message);
  const [messageEn, setMessageEn] = useState(
    notification && notification.messageEn
  );
  const [messagePl, setMessagePl] = useState(
    notification && notification.messagePl
  );
  const handleSubmit = useCallback(() => {
    setLoading(true);
    form.validateFields((err, values) => {
      if (!err) {
        if (message && messageEn) {
          const data = {
            ...values,
            id: notification && notification.id,
            validTo: getDateFromDatePicker(values.validTo),
            message,
            messageEn,
            messagePl,
          };
          (notification
            ? put(`notification/${notification.id}`, data)
            : post("notification", data)
          )
            .then((r) => {
              setLoading(false);
              if (checkHttpStatus(r) && checkResponse(r)) {
                success(
                  intl.formatMessage({
                    id: "notification.save.success",
                    defaultMessage: "Notifikace byla úspěšně uložena.",
                  })
                );
                r.data.id && push(`/notification/${r.data.id}/edit`);
              } else {
                error(
                  intl.formatMessage({
                    id: "notification.save.error",
                    defaultMessage: "Při ukládání notifikace nastala chyba!",
                  }),
                  10
                );
              }
            })
            .catch((err) => {
              setLoading(false);
              error(
                intl.formatMessage({
                  id: "notification.save.error",
                  defaultMessage: "Při ukládání notifikace nastala chyba!",
                }),
                10
              );
              handleError(err);
            });
        } else {
          setLoading();
          warn(
            intl.formatMessage({
              id: "notification.message.empty",
              defaultMessage: "Vyplňte prosím CZ a EN obsah!",
            })
          );
        }
      } else {
        setLoading();
      }
    });
  }, [form, intl, push, message, messageEn, messagePl, notification]);

  return (
    <Row>
      <Col span={24}>
        <TableHeader
          title={
            notification ? (
              <FormattedMessage
                id="notification.edit"
                defaultMessage="Editace notifikace"
              />
            ) : (
              <FormattedMessage
                id="notification.new"
                defaultMessage="Nová notifikace"
              />
            )
          }
        />
      </Col>
      <Col span={24}>
        <Row type="flex" justify="center">
          <Col xxl={12} sm={18}>
            <Form {...formItemLayout}>
              <InputItem
                allowClear
                fieldName="name"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                initialValue={notification && notification.name}
                label={intl.formatMessage({
                  id: "notification.name",
                  defaultMessage: "Název notifikace",
                })}
                message={intl.formatMessage({
                  id: "notification.name.empty",
                  defaultMessage: "Zadejte název.",
                })}
              />
              <Row>
                <Col span={6}>
                  <CheckboxItem
                    fieldName="displayToUsers"
                    formItemLayout={formItemLayout}
                    getFieldDecorator={getFieldDecorator}
                    initialValue={
                      notification ? notification.displayToUsers : true
                    }
                    label={intl.formatMessage({
                      id: "notification.displayToUsers",
                      defaultMessage: "Zobrazeno uživatelům",
                    })}
                  />
                </Col>
                <Col>
                  <DateItem
                    optional
                    showTime
                    fromToday
                    zeroDefaultTime
                    fieldName="validTo"
                    label={
                      <InfoLabel
                        label={intl.formatMessage({
                          id: "notification.validTo",
                          defaultMessage: "Zobrazovat do",
                        })}
                        info={intl.formatMessage({
                          id: "notification.validTo.info",
                          defaultMessage:
                            "Datum a čas, do kterého se má tato notifikace uživatelům zobrazovat.",
                        })}
                      />
                    }
                    formItemLayout={formItemLayout}
                    initialDate={
                      notification?.validTo && moment(notification.validTo)
                    }
                    getFieldDecorator={getFieldDecorator}
                  />
                </Col>
              </Row>
              <Divider />
              <InputItem
                allowClear
                fieldName="title"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                initialValue={notification && notification.title}
                label={intl.formatMessage({
                  id: "notification.title.cz",
                  defaultMessage: "Titulek CZ",
                })}
                message={intl.formatMessage({
                  id: "notification.title.cz.empty",
                  defaultMessage: "Zadejte CZ titulek.",
                })}
              />
              <TextEditor
                required
                onSetContent={setMessage}
                formItemLayout={formItemLayout}
                initialValue={notification && notification.message}
                label={intl.formatMessage({
                  id: "notification.message.cz",
                  defaultMessage: "Obsah CZ",
                })}
              />
              <Divider />
              <InputItem
                allowClear
                fieldName="titleEn"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                initialValue={notification && notification.titleEn}
                label={intl.formatMessage({
                  id: "notification.title.en",
                  defaultMessage: "Titulek",
                })}
                message={intl.formatMessage({
                  id: "notification.title.en.empty",
                  defaultMessage: "Zadejte titulek.",
                })}
              />
              <TextEditor
                required
                onSetContent={setMessageEn}
                formItemLayout={formItemLayout}
                initialValue={notification && notification.messageEn}
                label={intl.formatMessage({
                  id: "notification.message.en",
                  defaultMessage: "Obsah EN",
                })}
              />
              <Divider />
              <InputItem
                allowClear
                fieldName="titlePl"
                formItemLayout={formItemLayout}
                getFieldDecorator={getFieldDecorator}
                initialValue={notification && notification.titlePl}
                label={intl.formatMessage({
                  id: "notification.title.pl",
                  defaultMessage: "Titulek",
                })}
              />
              <TextEditor
                onSetContent={setMessagePl}
                formItemLayout={formItemLayout}
                initialValue={notification && notification.messagePl}
                label={intl.formatMessage({
                  id: "notification.message.pl",
                  defaultMessage: "Obsah PL",
                })}
              />
              <Divider />
              <Button type="primary" loading={loading} onClick={handleSubmit}>
                {intl.formatMessage({
                  id: "messages.save",
                  defaultMessage: "Uložit",
                })}
              </Button>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default Form.create()(connect(null, { push })(Notification));
